import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X, Check } from 'lucide-react';
import { FormField } from './FormField';
import { MapWidget } from './MapWidget';
import { ProductSelector } from './ProductSelector';
import { InvoiceGenerator } from './InvoiceGenerator';
interface OrderFormProps {
  suggestions: {
    customerName: string;
    date: string;
    jobType: string;
    orderType: string;
    productType: string;
    quantity: string;
    estimate: string;
    address: string;
  };
  onSendPreview: () => void;
  canSendPreview: boolean;
  orderConfirmed: boolean;
  formData: {
    customerName: string;
    date: string;
    jobType: string;
    orderType: string;
    productCategory: string;
    selectedProducts: Array<{
      productId: string;
      quantity: number;
    }>;
    estimate: string;
    address: string;
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    customerName: string;
    date: string;
    jobType: string;
    orderType: string;
    productCategory: string;
    selectedProducts: Array<{
      productId: string;
      quantity: number;
    }>;
    estimate: string;
    address: string;
  }>>;
}
export const OrderForm: React.FC<OrderFormProps> = ({
  suggestions,
  onSendPreview,
  canSendPreview,
  orderConfirmed,
  formData,
  setFormData
}) => {
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true);
  const [contactConfirmed, setContactConfirmed] = useState(false);
  const [contactDetails, setContactDetails] = useState({
    email: '',
    contact: ''
  });
  const [invoiceStatus, setInvoiceStatus] = useState('unpaid');
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setIsDetailsExpanded(false);
  };
  const handleAcceptSuggestion = (field: string) => {
    handleChange(field, suggestions[field]);
  };
  const handleRejectSuggestion = (field: string) => {
    // Create a new suggestions object without the rejected suggestion
    const updatedSuggestions = {
      ...suggestions
    };
    updatedSuggestions[field] = '';
  };
  const handleConfirmContact = () => {
    setContactConfirmed(true);
    setInvoiceStatus('generated');
  };
  const hasSuggestions = () => {
    return suggestions.customerName || suggestions.date || suggestions.address || suggestions.jobType || suggestions.orderType || suggestions.estimate;
  };
  return <div className="bg-card p-4 md:p-6 rounded-lg shadow-md min-h-[calc(100vh-8rem)] lg:h-[calc(100vh-8rem)]" data-id="element-68">
      <h2 className="text-xl font-semibold mb-4" data-id="element-69">
        {isSubmitted ? 'Order Details & Invoice' : 'Order'}
      </h2>
      <form onSubmit={handleSubmit} className="h-[calc(100%-2rem)] flex flex-col" data-id="element-70">
        {isSubmitted && <div className="mb-6 border rounded-lg p-4" data-id="element-71">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsDetailsExpanded(!isDetailsExpanded)} data-id="element-72">
              <div className="space-y-1" data-id="element-73">
                <h3 className="font-medium" data-id="element-74">Order Summary</h3>
                <p className="text-sm text-muted-foreground" data-id="element-75">
                  {formData.customerName} - {formData.date}
                </p>
              </div>
              {isDetailsExpanded ? <ChevronUp className="w-5 h-5" data-id="element-76" /> : <ChevronDown className="w-5 h-5" data-id="element-77" />}
            </div>
            {isDetailsExpanded && <div className="mt-4 space-y-6" data-id="element-78">
                <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-79">
                  <div className="space-y-4" data-id="element-80">
                    <div data-id="element-81">
                      <span className="text-muted-foreground block mb-1" data-id="element-82">
                        Customer Details
                      </span>
                      <p className="font-medium" data-id="element-83">{formData.customerName}</p>
                      <p className="text-muted-foreground" data-id="element-84">
                        {formData.address}
                      </p>
                    </div>
                    <div data-id="element-85">
                      <span className="text-muted-foreground block mb-1" data-id="element-86">
                        Order Date
                      </span>
                      <p data-id="element-87">{formData.date}</p>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-88">
                    <div data-id="element-89">
                      <span className="text-muted-foreground block mb-1" data-id="element-90">
                        Products Selected
                      </span>
                      <div className="space-y-1" data-id="element-91">
                        {formData.selectedProducts.map((product, index) => <p key={index} data-id="element-92">
                            {product.quantity}x {product.productId}
                          </p>)}
                      </div>
                    </div>
                    <div data-id="element-93">
                      <span className="text-muted-foreground block mb-1" data-id="element-94">
                        Estimated Total
                      </span>
                      <p className="font-medium" data-id="element-95">{formData.estimate}</p>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t" data-id="element-96">
                  <span className="text-muted-foreground block mb-2 text-sm" data-id="element-97">
                    Order Type
                  </span>
                  <div className="flex gap-4" data-id="element-98">
                    <div className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded-md text-sm" data-id="element-99">
                      {formData.jobType || 'Not specified'}
                    </div>
                    <div className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded-md text-sm" data-id="element-100">
                      {formData.orderType || 'Not specified'}
                    </div>
                  </div>
                </div>
              </div>}
          </div>}
        {!isSubmitted ? <>
            <div className="space-y-6 flex-1 overflow-y-auto pr-2" data-id="element-101">
              <FormField label="Customer Name" value={formData.customerName} suggestion={suggestions.customerName} onChange={value => handleChange('customerName', value)} onAccept={() => handleAcceptSuggestion('customerName')} onReject={() => handleRejectSuggestion('customerName')} data-id="element-102" />
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-103">
                <div className="md:col-span-3 space-y-4" data-id="element-104">
                  <FormField label="Address" value={formData.address} suggestion={suggestions.address} onChange={value => handleChange('address', value)} onAccept={() => handleAcceptSuggestion('address')} onReject={() => handleRejectSuggestion('address')} data-id="element-105" />
                  <FormField label="Date" value={formData.date} suggestion={suggestions.date} onChange={value => handleChange('date', value)} onAccept={() => handleAcceptSuggestion('date')} onReject={() => handleRejectSuggestion('date')} type="date" data-id="element-106" />
                </div>
                <div className="md:col-span-2" data-id="element-107">
                  <label className="block text-sm font-medium mb-1" data-id="element-108">Map</label>
                  <MapWidget address={formData.address} data-id="element-109" />
                </div>
              </div>
              <div data-id="element-110">
                <label className="block text-sm font-medium mb-3" data-id="element-111">
                  Product Selection
                </label>
                <ProductSelector selectedCategory={formData.productCategory} selectedProducts={formData.selectedProducts} onCategoryChange={category => handleChange('productCategory', category)} onProductChange={selections => setFormData(prev => ({
              ...prev,
              selectedProducts: selections
            }))} data-id="element-112" />
              </div>
              <FormField label="Estimate" value={formData.estimate} suggestion={suggestions.estimate} onChange={value => handleChange('estimate', value)} onAccept={() => handleAcceptSuggestion('estimate')} onReject={() => handleRejectSuggestion('estimate')} data-id="element-113" />
            </div>
            <div className="border-t mt-6 pt-6" data-id="element-114">
              <div className="flex flex-col md:flex-row gap-4" data-id="element-115">
                <button type="submit" disabled={!orderConfirmed} className="flex-1 bg-primary text-primary-foreground py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-116">
                  Submit Order
                </button>
                {canSendPreview && !orderConfirmed ? <button type="button" onClick={onSendPreview} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-opacity-90 transition-colors" data-id="element-117">
                    Send Preview to Customer
                  </button> : orderConfirmed ? <div className="space-y-3" data-id="element-118">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-center gap-2 text-emerald-600" data-id="element-119">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-120"></div>
                      <span className="text-sm font-medium" data-id="element-121">
                        Details confirmed by customer
                      </span>
                    </div>
                    <button type="button" onClick={() => {
                handleAcceptSuggestion('customerName');
                handleAcceptSuggestion('date');
                handleAcceptSuggestion('address');
                handleAcceptSuggestion('jobType');
                handleAcceptSuggestion('orderType');
                handleAcceptSuggestion('estimate');
              }} disabled={isSubmitted || !hasSuggestions()} className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-122">
                      Accept All
                    </button>
                  </div> : <button type="button" disabled className="px-4 py-2 rounded-md bg-secondary text-secondary-foreground opacity-50 cursor-not-allowed" data-id="element-123">
                    Send Preview to Customer
                  </button>}
              </div>
            </div>
          </> : <div className="flex-1" data-id="element-124">
            <h3 className="text-lg font-medium mb-4" data-id="element-125">Contact Confirmation</h3>
            {!contactConfirmed ? <div className="space-y-4" data-id="element-126">
                <div className="p-3 md:p-4 bg-muted/50 rounded-lg" data-id="element-127">
                  <p className="text-sm text-muted-foreground mb-3" data-id="element-128">
                    Please confirm the contact details before generating the
                    invoice:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-129">
                    <FormField label="Email Address" value={contactDetails.email} suggestion={suggestions.email} onChange={value => setContactDetails(prev => ({
                ...prev,
                email: value
              }))} onAccept={() => {
                setContactDetails(prev => ({
                  ...prev,
                  email: suggestions.email
                }));
              }} type="email" data-id="element-130" />
                    <FormField label="Point of Contact" value={contactDetails.contact} suggestion={suggestions.contact} onChange={value => setContactDetails(prev => ({
                ...prev,
                contact: value
              }))} onAccept={() => {
                setContactDetails(prev => ({
                  ...prev,
                  contact: suggestions.contact
                }));
              }} data-id="element-131" />
                  </div>
                  <button onClick={handleConfirmContact} disabled={!contactDetails.email || !contactDetails.contact} className="mt-4 w-full bg-primary text-primary-foreground py-2.5 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-132">
                    Send Invoice
                  </button>
                </div>
              </div> : invoiceStatus === 'generated' ? <div className="space-y-4" data-id="element-133">
                <div className="p-3 md:p-4 bg-emerald-50 border-emerald-200 border rounded-lg" data-id="element-134">
                  <div className="flex items-center gap-2 text-emerald-700" data-id="element-135">
                    <Check className="w-5 h-5" data-id="element-136" />
                    <p className="font-medium" data-id="element-137">Order Created Successfully</p>
                  </div>
                  <p className="text-sm text-emerald-600 mt-1" data-id="element-138">
                    Invoice has been sent to {contactDetails.email}. A copy of
                    the order details has been saved to our system.
                  </p>
                </div>
                <InvoiceGenerator orderDetails={{
            ...formData,
            billToEmail: contactDetails.email,
            primaryContact: contactDetails.contact
          }} status="unpaid" data-id="element-139" />
              </div> : <div className="space-y-4" data-id="element-140">
                <div className="p-3 md:p-4 bg-muted rounded-lg" data-id="element-141">
                  <h4 className="font-medium mb-2" data-id="element-142">
                    Confirmed Contact Details
                  </h4>
                  <div className="space-y-1 text-sm text-muted-foreground" data-id="element-143">
                    <p data-id="element-144">Email: {contactDetails.email}</p>
                    <p data-id="element-145">Contact: {contactDetails.contact}</p>
                  </div>
                </div>
                <InvoiceGenerator orderDetails={{
            ...formData,
            billToEmail: contactDetails.email,
            primaryContact: contactDetails.contact
          }} status="unpaid" onGenerate={() => setInvoiceStatus('generated')} data-id="element-146" />
              </div>}
          </div>}
      </form>
    </div>;
};